import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import styles from './styles/ClassroomAssignmentsModal.module.css';
import { ClassroomContext } from '../../context/ClassroomContext';
import AuthContext from '../../context/AuthContext';
import Loading from '../loading/Loading';
import { FaSave, FaTimes, FaSearch, FaCheckSquare, FaRegSquare, FaImage, FaUserCircle } from 'react-icons/fa';

const ClassroomAssignmentsModal = ({ onClose, assignment }) => {
    const { classroom, createAssignment, updateAssignment } = useContext(ClassroomContext);
    const { userId } = useContext(AuthContext);
    const isEditMode = Boolean(assignment);

    const [title, setTitle] = useState(assignment ? assignment.title : '');
    const [description, setDescription] = useState(assignment ? assignment.description : '');
    const [dueDate, setDueDate] = useState(
        assignment && assignment.dueDate ? assignment.dueDate.substring(0, 10) : ''
    );
    const [selectedScenario, setSelectedScenario] = useState(assignment ? assignment.scenario._id : null);
    const [scenarios, setScenarios] = useState([]);
    const [scenarioSearch, setScenarioSearch] = useState('');
    const [assignAll, setAssignAll] = useState(true);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [loading, setIsLoading] = useState(true);

    
    useEffect(() => {
        const fetchScenarios = async () => {
            if (userId) {
                try {
                    const response = await axios.get(
                        `${process.env.REACT_APP_API_URL_LOCAL}/api/scenarios/my`
                    );
                    setScenarios(response.data);
                } catch (error) {
                    console.error('Error fetching teacher scenarios:', error);
                } finally {
                    setIsLoading(false);
                }
            } else {
                setIsLoading(false);
            }
            console.log('classroom students: ', classroom.students);
        };
        fetchScenarios();
    }, [userId, classroom.students]);

    
    useEffect(() => {
        if (isEditMode && classroom && classroom.students) {
            if (assignment.students.length === classroom.students.length) {
                setAssignAll(true);
            } else {
                setAssignAll(false);
                setSelectedStudents(assignment.students.map(student => student._id));
            }
        }
    }, [assignment, classroom, isEditMode]);

    const toggleStudentSelection = (studentId) => {
        if (selectedStudents.includes(studentId)) {
            setSelectedStudents(selectedStudents.filter(id => id !== studentId));
        } else {
            setSelectedStudents([...selectedStudents, studentId]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log("create?");

        if (!title || !selectedScenario) {
            alert("Title and Scenario are required.");
            return;
        }

        
        setIsLoading(true);

        const assignmentData = {
            title,
            description,
            dueDate: dueDate ? new Date(dueDate) : null,
            scenario: selectedScenario,
            students: assignAll
                ? classroom.students.map(student => student._id || student.id)
                : selectedStudents
        };

        try {
            if (isEditMode) {
                await updateAssignment(assignment._id, assignmentData);
            } else {
                
                await createAssignment(classroom._id, assignmentData);

                
                const assignees = assignAll
                    ? classroom.students
                    : classroom.students.filter(student =>
                          selectedStudents.includes(student._id || student.id)
                      );

                
                await axios.post(`${process.env.REACT_APP_API_URL_LOCAL}/api/notifications/sendAssignmentToStudents`, {
                    assignees,
                    classroomId: classroom._id,
                    classroomName: classroom.name,
                    assignmentTitle: title
                });
            }
            setIsLoading(false);
            onClose();
        } catch (error) {
            console.error('Error saving assignment:', error);
            setIsLoading(false);
        }
    };

    
    const filteredScenarios = scenarios.filter((scenario) =>
        scenario.title.toLowerCase().includes(scenarioSearch.toLowerCase())
    );

    if (loading) {
        return (
            <div className={styles.modalOverlay}>
                <div
                    className={styles.modalContent}
                    style={{ alignItems: 'center', justifyContent: 'center' }}
                >
                    <Loading />
                </div>
            </div>
        );
    }
    
    return (
        <div className={styles.modalOverlay}>
            <div
                className={styles.modalContent}
                role="dialog"
                aria-modal="true"
                aria-labelledby="assignmentModalTitle"
            >
                <h2 id="assignmentModalTitle" className={styles.modalTitle}>
                    {isEditMode ? 'Edit Assignment' : 'Create New Assignment'}
                </h2>
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="assignmentTitle" className={styles.label}>
                            Title <span className={styles.requiredIndicator}>*</span>
                        </label>
                        <input
                            id="assignmentTitle"
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className={styles.inputField}
                            required
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label htmlFor="assignmentDescription" className={styles.label}>
                            Description (Optional)
                        </label>
                        <textarea
                            id="assignmentDescription"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className={styles.textarea}
                            rows={3}
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label htmlFor="assignmentDueDate" className={styles.label}>
                            Due Date (Optional)
                        </label>
                        <input
                            id="assignmentDueDate"
                            type="date"
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                            className={styles.inputField}
                        />
                    </div>
                    <fieldset className={styles.fieldset}>
                        <legend className={styles.legend}>Assign To</legend>
                        <div className={styles.assignSection}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    className={styles.checkboxInput}
                                    checked={assignAll}
                                    onChange={(e) => setAssignAll(e.target.checked)}
                                />
                                <span className={styles.checkboxMark}>
                                    {assignAll ? <FaCheckSquare /> : <FaRegSquare />}
                                </span>
                                <span className={styles.checkboxText}>
                                    Assign to all students in classroom
                                </span>
                            </label>
                            {!assignAll && classroom?.students?.length > 0 && (
                                <div className={styles.studentsListContainer}>
                                    <p className={styles.subLabel}>
                                        Select individual students:
                                    </p>
                                    <div
                                        className={`${styles.listWrapper} ${styles.studentsListWrapper}`}
                                    >
                                        <ul className={styles.list}>
                                            {classroom.students.map((student) => (
                                                <li
                                                    key={student._id || student.id}
                                                    className={`${styles.listItem} ${
                                                        styles.studentItem
                                                    } ${
                                                        selectedStudents.includes(
                                                            student._id || student.id
                                                        )
                                                            ? styles.selected
                                                            : ''
                                                    }`}
                                                    onClick={() =>
                                                        toggleStudentSelection(
                                                            student._id || student.id
                                                        )
                                                    }
                                                    tabIndex={0}
                                                    role="checkbox"
                                                    aria-checked={selectedStudents.includes(
                                                        student._id || student.id
                                                    )}
                                                    onKeyPress={(e) => {
                                                        if (
                                                            e.key === 'Enter' ||
                                                            e.key === ' '
                                                        )
                                                            toggleStudentSelection(
                                                                student._id ||
                                                                    student.id
                                                            );
                                                    }}
                                                >
                                                    {student.profilePicture ? (
                                                        <img
                                                            src={student.profilePicture}
                                                            alt=""
                                                            className={
                                                                styles.studentAvatar
                                                            }
                                                        />
                                                    ) : (
                                                        <div
                                                            className={
                                                                styles.studentAvatarPlaceholder
                                                            }
                                                        >
                                                            <FaUserCircle />
                                                        </div>
                                                    )}
                                                    <span className={styles.listItemLabel}>
                                                        {student.username ||
                                                            'Unnamed Student'}
                                                    </span>
                                                    {selectedStudents.includes(
                                                        student._id || student.id
                                                    ) && (
                                                        <FaCheckSquare
                                                            className={styles.selectedIcon}
                                                        />
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}
                            {!assignAll &&
                                (!classroom?.students ||
                                    classroom.students.length === 0) && (
                                    <p
                                        className={styles.listItemEmpty}
                                        style={{ marginTop: '10px' }}
                                    >
                                        No students found in this classroom.
                                    </p>
                                )}
                        </div>
                    </fieldset>
                    <fieldset className={styles.fieldset}>
                        <legend className={styles.legend}>
                            Choose Assignment <span className={styles.requiredIndicator}>*</span>
                        </legend>
                        <div className={styles.searchWrapper}>
                            <FaSearch className={styles.searchIcon} />
                            <input
                                type="search"
                                placeholder="Search scenarios by title..."
                                value={scenarioSearch}
                                onChange={(e) => setScenarioSearch(e.target.value)}
                                className={`${styles.inputField} ${styles.searchInput}`}
                                aria-label="Search Scenarios"
                            />
                        </div>
                        <div
                            className={`${styles.listWrapper} ${styles.scenarioListWrapper}`}
                        >
                            <ul className={styles.list}>
                                {filteredScenarios.length === 0 && (
                                    <li className={styles.listItemEmpty}>
                                        No scenarios match your search.
                                    </li>
                                )}
                                {filteredScenarios.map((scenario) => (
                                    <li
                                        key={scenario._id}
                                        className={`${styles.listItem} ${styles.scenarioItem} ${
                                            selectedScenario === scenario._id
                                                ? styles.selected
                                                : ''
                                        }`}
                                        onClick={() => setSelectedScenario(scenario._id)}
                                        tabIndex={0}
                                        role="radio"
                                        aria-checked={selectedScenario === scenario._id}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ')
                                                setSelectedScenario(scenario._id);
                                        }}
                                    >
                                        {scenario.image ? (
                                            <img
                                                src={scenario.image}
                                                alt=""
                                                className={styles.scenarioImage}
                                            />
                                        ) : (
                                            <div
                                                className={styles.scenarioImagePlaceholder}
                                            >
                                                <FaImage />
                                            </div>
                                        )}
                                        <span className={styles.listItemLabel}>
                                            {scenario.title || 'Untitled Scenario'}
                                        </span>
                                        {selectedScenario === scenario._id && (
                                            <FaCheckSquare
                                                className={styles.selectedIcon}
                                            />
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </fieldset>
                </form>
                <footer className={styles.modalFooter}>
                    <div className={styles.buttonGroup}>
                        <button
                            type="button"
                            onClick={onClose}
                            className={`${styles.button} ${styles.buttonSecondary}`}
                        >
                            <FaTimes /> Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className={`${styles.button} ${styles.buttonPrimary}`}
                            disabled={
                                !title?.trim() ||
                                !selectedScenario ||
                                (!assignAll &&
                                    selectedStudents.length === 0)
                            }
                        >
                            <FaSave />{' '}
                            {isEditMode
                                ? 'Update Assignment'
                                : 'Create Assignment'}
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
    
};

export default ClassroomAssignmentsModal;
